import { useEffect, useRef, useState } from 'react'

import { type FormikProps } from 'formik'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import styled from 'styled-components'

import { useSettingsCheck } from 'api/verification/useSettingsCheck/useSettingsCheck'
import idExample from 'assets/svg/id-example.svg'
import idHoldingExample from 'assets/svg/id-holding-example.svg'
import { IconCircleCheckmark } from 'assets/svg/sprite'
import DateModal from 'components/UI/Modal/Date/DateModal'
import PhotoUploadCard from 'components/UI/PhotoUploadCard/PhotoUploadCard'
import { RadioButton } from 'components-ref/atoms'
import { useModalContext } from 'context/modalContext'
import { useIdVerificationUpload } from 'services/verification/useIdVerificationUpload'
import { type PersonalInformationFormValues } from 'services/verification/usePersonalInformationForm'
import colors from 'styles/colors'
import screens from 'styles/screens'

interface VerificationPersonalInformationFormProps {
  formik: FormikProps<PersonalInformationFormValues & { agreeToCreatorTerms: boolean }>
  settingsValue?: number
}

type ParsedPlace = {
  street: string
  city: string
  stateLong: string
  zip: string
  countryLong: string
}

type AddressSuggestion = {
  id: string
  mainText: string
  secondaryText: string
  fullText: string
  prediction: any
}

const loadGoogleMapsScript = async (apiKey?: string) =>
  await new Promise<void>((resolve, reject) => {
    if (!apiKey) {
      reject(new Error('Missing Google Maps API key'))
      return
    }

    if (
      typeof window?.google?.maps !== 'undefined' &&
      typeof window.google.maps.importLibrary === 'function'
    ) {
      resolve()
      return
    }

    const existing = document.querySelector(
      'script[data-google-maps-script="true"]'
    )

    if (existing) {
      existing.addEventListener('load', () => resolve(), { once: true })
      existing.addEventListener(
        'error',
        () => reject(new Error('Google Maps script failed to load')),
        { once: true }
      )
      return
    }

    const script = document.createElement('script')
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&loading=async`
    script.async = true
    script.defer = true
    script.setAttribute('data-google-maps-script', 'true')
    script.onload = () => resolve()
    script.onerror = () => reject(new Error('Google Maps script failed to load'))

    document.head.appendChild(script)
  })

const isMapsReady = () =>
  typeof window?.google?.maps !== 'undefined' &&
  typeof window.google.maps.importLibrary === 'function'

const getAddressPart = (components: any[] = [], type: string) =>
  components.find(
    component => Array.isArray(component.types) && component.types.includes(type)
  )

const getLongName = (components: any[] = [], type: string) =>
  getAddressPart(components, type)?.long_name ??
  getAddressPart(components, type)?.longText ??
  ''

const getPlaceComponents = (place: any): any[] => {
  if (Array.isArray(place?.address_components)) return place.address_components
  if (Array.isArray(place?.addressComponents)) return place.addressComponents
  return []
}

const parsePlace = (place: any): ParsedPlace => {
  const components = getPlaceComponents(place)
  const streetNumber = getLongName(components, 'street_number')
  const route = getLongName(components, 'route')

  return {
    street: [streetNumber, route].filter(Boolean).join(' ').trim(),
    city:
      getLongName(components, 'locality') ||
      getLongName(components, 'postal_town') ||
      getLongName(components, 'sublocality'),
    stateLong: getLongName(components, 'administrative_area_level_1'),
    zip: getLongName(components, 'postal_code'),
    countryLong: getLongName(components, 'country'),
  }
}

const getPredictionText = (value: any) => value?.text ?? ''

export function VerificationPersonalInformationForm({
  formik
}: VerificationPersonalInformationFormProps) {
  const { t } = useTranslation()
  const { data: settingsValue } = useSettingsCheck()
  const {
    handleImageChange,
    handleHoldImageChange,
    idHoldProgressValues,
    idProgressValues
  } = useIdVerificationUpload(formik)
  const modalData = useModalContext()

  const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY

  const documentOptions = [
    { value: 'passport', label: t('passport') },
    { value: 'personalId', label: t('personalId') },
    { value: 'driversLicense', label: t('driversLicense') },
  ]

  const selectedDocumentLabel =
    documentOptions.find(option => option.value === formik.values.documentType)?.label ??
    ''

  const [showDocumentOptions, setShowDocumentOptions] = useState(false)
  const [isMapsScriptReady, setIsMapsScriptReady] = useState(false)
  const [addressQuery, setAddressQuery] = useState(formik.values.address ?? '')
  const [addressSuggestions, setAddressSuggestions] = useState<AddressSuggestion[]>([])
  const [isAddressLoading, setIsAddressLoading] = useState(false)
  const [showSuggestions, setShowSuggestions] = useState(false)

  const addressInputRef = useRef<HTMLInputElement | null>(null)
  const autocompleteRef = useRef<HTMLDivElement | null>(null)
  const documentSelectRef = useRef<HTMLDivElement | null>(null)
  const sessionTokenRef = useRef<any>(null)
  const requestSeqRef = useRef(0)
  const skipNextAddressFetchRef = useRef(false)

  const formatDOB = (dob: string) => {
    if (!dob) return ''
    const date = new Date(dob)
    return date.toDateString()
  }
  const eighteenYearsAgo = new Date()
  eighteenYearsAgo.setFullYear(eighteenYearsAgo.getFullYear() - 18)

  function openDatePickerModal(title: string, key: string, hasDelete = false) {
    modalData.addModal(
      title,
      <DateModal
        noMinDate={true}
        noNextStep={true}
        deleteFn={
          hasDelete
            ? () => {
                formik.setFieldValue(key, '')
              }
            : undefined
        }
        confirmFn={(val) => {
          formik.setFieldValue(key, val)
        }}
        newDateVal={key === 'dateOfBirth' ? eighteenYearsAgo : undefined}
      />,
      false,
      false,
      'verification-date-picker'
    )
  }

  const fetchAddressSuggestions = async (input: string) => {
    const trimmed = input.trim()

    if (!trimmed || !isMapsReady()) {
      setAddressSuggestions([])
      setShowSuggestions(false)
      setIsAddressLoading(false)
      return
    }

    const requestId = ++requestSeqRef.current
    setIsAddressLoading(true)

    try {
      const placesLib = (await google.maps.importLibrary('places')) as any

      if (!sessionTokenRef.current) {
        sessionTokenRef.current = new placesLib.AutocompleteSessionToken()
      }

      const request: any = {
        input: trimmed,
        sessionToken: sessionTokenRef.current,
        language: 'en',
      }

      const response =
        await placesLib.AutocompleteSuggestion.fetchAutocompleteSuggestions(request)

      if (requestId !== requestSeqRef.current) return

      const mapped: AddressSuggestion[] = (response?.suggestions ?? [])
        .map((suggestion: any, index: number) => {
          const prediction = suggestion?.placePrediction
          if (!prediction) return null

          return {
            id: `${prediction.placeId ?? 'prediction'}-${index}`,
            mainText: getPredictionText(prediction.mainText),
            secondaryText: getPredictionText(prediction.secondaryText),
            fullText: getPredictionText(prediction.text),
            prediction,
          }
        })
        .filter(Boolean) as AddressSuggestion[]

      setAddressSuggestions(mapped)
      setShowSuggestions(mapped.length > 0)
    } catch (error) {
      console.error('[VerificationAddressAutocomplete] failed to fetch suggestions', error)
      setAddressSuggestions([])
      setShowSuggestions(false)
    } finally {
      if (requestId === requestSeqRef.current) {
        setIsAddressLoading(false)
      }
    }
  }

  const handleAddressSuggestionSelect = async (suggestion: AddressSuggestion) => {
    try {
      const place = suggestion.prediction.toPlace()

      await place.fetchFields({
        fields: ['addressComponents'],
      })

      const parsed = parsePlace(place)

      skipNextAddressFetchRef.current = true

      await formik.setFieldValue(
        'address',
        parsed.street || formik.values.address || suggestion.mainText || suggestion.fullText
      )
      await formik.setFieldValue('city', parsed.city || '')
      await formik.setFieldValue('stateProvince', parsed.stateLong || '')
      await formik.setFieldValue('postal', parsed.zip || '')
      await formik.setFieldValue('country', parsed.countryLong || '')

      setAddressQuery(
        parsed.street || suggestion.mainText || suggestion.fullText || formik.values.address || ''
      )
      setAddressSuggestions([])
      setShowSuggestions(false)
      sessionTokenRef.current = null

      if (addressInputRef.current) {
        addressInputRef.current.blur()
      }
    } catch (error) {
      console.error('[VerificationAddressAutocomplete] failed to fetch place details', error)
    }
  }

  useEffect(() => {
    let mounted = true

    loadGoogleMapsScript(GOOGLE_MAPS_API_KEY)
      .then(() => {
        if (mounted) {
          setIsMapsScriptReady(true)
        }
      })
      .catch(error => {
        console.error('[VerificationAddressAutocomplete] Google Maps script load failed', error)
        if (mounted) {
          setIsMapsScriptReady(false)
        }
      })

    return () => {
      mounted = false
    }
  }, [GOOGLE_MAPS_API_KEY])

  useEffect(() => {
    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node | null

      if (!autocompleteRef.current?.contains(target)) {
        setShowSuggestions(false)
      }

      if (!documentSelectRef.current?.contains(target)) {
        setShowDocumentOptions(false)
      }
    }

    document.addEventListener('mousedown', handlePointerDown)
    document.addEventListener('touchstart', handlePointerDown)

    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
      document.removeEventListener('touchstart', handlePointerDown)
    }
  }, [])

  useEffect(() => {
    if (addressQuery !== formik.values.address) {
      setAddressQuery(formik.values.address ?? '')
    }
  }, [formik.values.address])

  useEffect(() => {
    if (!isMapsScriptReady || !addressQuery.trim()) {
      setAddressSuggestions([])
      setShowSuggestions(false)
      setIsAddressLoading(false)
      return
    }

    if (skipNextAddressFetchRef.current) {
      skipNextAddressFetchRef.current = false
      return
    }

    const timeoutId = setTimeout(() => {
      fetchAddressSuggestions(addressQuery)
    }, 220)

    return () => clearTimeout(timeoutId)
  }, [addressQuery, isMapsScriptReady])

  return (
    <form>
      <FormDivider />
      <Paragraph>{t('fillInnYourLegalName')}</Paragraph>

      <InputContainer>
        <InputLabel $filled={!!formik.values.firstName} htmlFor='firstName'>
          {t('firstName')} *
        </InputLabel>
        <Input
          id='firstName'
          name='firstName'
          type='text'
          value={formik.values.firstName}
          onChange={formik.handleChange}
          $filled={!!formik.values.firstName}
        />
      </InputContainer>

      <InputContainer>
        <InputLabel $filled={!!formik.values.lastName} htmlFor='lastName'>
          {t('lastName')} *
        </InputLabel>
        <Input
          name='lastName'
          id='lastName'
          type='text'
          value={formik.values.lastName}
          onChange={formik.handleChange}
          $filled={!!formik.values.lastName}
        />
      </InputContainer>

      <Paragraph $fontSize='1.2rem'>
        {t('ifYouWouldLikeToChangeYourCountry')}{' '}
        <Link className='formLink' to='/support/home'>
          {t('customerSupport')}
        </Link>
      </Paragraph>

      <AutocompleteWrapper ref={autocompleteRef}>
        <InputContainer>
          <InputLabel $filled={!!formik.values.address} htmlFor='address'>
            {t('address')}
          </InputLabel>
          <Input
            ref={addressInputRef}
            name='address'
            id='address'
            type='text'
            value={addressQuery}
            onChange={(e) => {
              skipNextAddressFetchRef.current = false
              setAddressQuery(e.target.value)
              formik.setFieldValue('address', e.target.value)
            }}
            onFocus={() => {
              if (addressSuggestions.length > 0) {
                setShowSuggestions(true)
              }
            }}
            autoComplete='off'
            $filled={!!formik.values.address}
          />
        </InputContainer>

        {showSuggestions && (addressSuggestions.length > 0 || isAddressLoading) && (
          <SuggestionsDropdown>
            {isAddressLoading && addressSuggestions.length === 0 ? (
              <SuggestionLoading>Searching...</SuggestionLoading>
            ) : (
              addressSuggestions.map(item => (
                <SuggestionItem
                  key={item.id}
                  type='button'
                  onMouseDown={(e) => {
                    e.preventDefault()
                    handleAddressSuggestionSelect(item)
                  }}
                >
                  <SuggestionMain>{item.mainText || item.fullText}</SuggestionMain>
                  {!!item.secondaryText && (
                    <SuggestionSecondary>{item.secondaryText}</SuggestionSecondary>
                  )}
                </SuggestionItem>
              ))
            )}
          </SuggestionsDropdown>
        )}
      </AutocompleteWrapper>

      <TwoColumnRow>
        <InputContainer>
          <InputLabel $filled={!!formik.values.city} htmlFor='city'>
            {t('city')}
          </InputLabel>
          <Input
            name='city'
            id='city'
            type='text'
            value={formik.values.city}
            onChange={formik.handleChange}
            $filled={!!formik.values.city}
          />
        </InputContainer>

        <InputContainer>
          <InputLabel $filled={!!formik.values.stateProvince} htmlFor='stateProvince'>
            {t('stateProvince')} *
          </InputLabel>
          <Input
            name='stateProvince'
            id='stateProvince'
            type='text'
            value={formik.values.stateProvince}
            onChange={formik.handleChange}
            $filled={!!formik.values.stateProvince}
          />
        </InputContainer>
      </TwoColumnRow>

      <TwoColumnRow>
        <InputContainer>
          <InputLabel $filled={!!formik.values.country} htmlFor='country'>
            {t('country')} *
          </InputLabel>
          <Input
            name='country'
            id='country'
            type='text'
            value={formik.values.country}
            onChange={formik.handleChange}
            $filled={!!formik.values.country}
          />
        </InputContainer>

        <InputContainer>
          <InputLabel $filled={!!formik.values.postal} htmlFor='postal'>
            {t('postalZip')} *
          </InputLabel>
          <Input
            name='postal'
            id='postal'
            type='text'
            value={formik.values.postal}
            onChange={formik.handleChange}
            $filled={!!formik.values.postal}
          />
        </InputContainer>
      </TwoColumnRow>

      <GroupWithBorder>
        <Paragraph $fontSize='1.2rem'>
          {t('thisInformationWillNotBeShown')}
        </Paragraph>

        <InputContainer>
          <InputLabel $filled={!!formik.values.social.twitter} htmlFor='social.twitter'>
            Twitter
          </InputLabel>
          <Input
            name='social.twitter'
            id='social.twitter'
            type='text'
            value={formik.values.social.twitter}
            onChange={formik.handleChange}
            $filled={!!formik.values.social.twitter}
          />
        </InputContainer>

        <InputContainer>
          <InputLabel $filled={!!formik.values.social.instagram} htmlFor='social.instagram'>
            Instagram
          </InputLabel>
          <Input
            name='social.instagram'
            id='social.instagram'
            type='text'
            value={formik.values.social.instagram}
            onChange={formik.handleChange}
            $filled={!!formik.values.social.instagram}
          />
        </InputContainer>

        <InputContainer>
          <InputLabel $filled={!!formik.values.social.other} htmlFor='social.other'>
            {t('other')}
          </InputLabel>
          <Input
            name='social.other'
            id='social.other'
            type='text'
            value={formik.values.social.other}
            onChange={formik.handleChange}
            $filled={!!formik.values.social.other}
          />
        </InputContainer>

        <div>
          <Paragraph $fontSize='1.2rem' $type='error'>
            {formik.errors.social && formik.touched.social ? 'Required' : ''}
          </Paragraph>
          <Paragraph $fontSize='1.2rem' $right $marginBottom='1rem'>
            {t('mustChooseOne')}
          </Paragraph>
        </div>
      </GroupWithBorder>

      <InputContainer>
        <InputLabel $filled={!!formik.values.dateOfBirth} htmlFor='dateOfBirth'>
          {t('dateOfBirth')}
        </InputLabel>
        <Input
          key='dateOfBirth'
          value={formatDOB(formik.values.dateOfBirth)}
          onClick={() => openDatePickerModal(t('dateOfBirth'), 'dateOfBirth')}
          $filled={!!formik.values.dateOfBirth}
          readOnly
        />
      </InputContainer>

      {settingsValue !== 1 && (
        <>
          <DocumentSelectContainer ref={documentSelectRef} $open={showDocumentOptions}>
            <DocumentSelectHeader
              type='button'
              onClick={() => setShowDocumentOptions(prev => !prev)}
            >
              <DocumentSelectLabel $filled={!!formik.values.documentType}>
                {t('documentType')}
              </DocumentSelectLabel>
              <DocumentSelectValue>{selectedDocumentLabel}</DocumentSelectValue>
            </DocumentSelectHeader>

            {showDocumentOptions && (
              <DocumentOptions>
                {documentOptions.map(option => (
                  <DocumentOption
                    key={option.value}
                    type='button'
                    $active={formik.values.documentType === option.value}
                    onClick={async () => {
                      await formik.setFieldValue('documentType', option.value)
                      await formik.setFieldTouched('documentType', true, false)
                      setShowDocumentOptions(false)
                    }}
                  >
                    {option.label}
                  </DocumentOption>
                ))}
              </DocumentOptions>
            )}
          </DocumentSelectContainer>

          <PhotoSection>
            <PhotoSectionTitle>
              <h3>{t('photoOfYourId')}</h3>
              {/* <span className='circle'>?</span> */}
            </PhotoSectionTitle>
            <Paragraph $fontSize='1.2rem'>{t('pleaseUploadAPhotoOfYourId')}</Paragraph>

            <input onChange={handleImageChange} type='file' id='idFile' hidden multiple />
            <SelectFileButton htmlFor='idFile'>{t('selectFile')}</SelectFileButton>

            {!(idProgressValues.length === 0) && (
              <PhotoUploadList>
                {idProgressValues.map((file, index) => {
                  return (
                    <PhotoUploadCard
                      key={index}
                      name={file.name}
                      progressValue={file.percentage}
                      type={file.type}
                      size={file.size}
                      abortFn={() => {
                        file.cancel?.()
                      }}
                    />
                  )
                })}
              </PhotoUploadList>
            )}

            <div>
              <PhotoExample>
                <ImageHolder>
                  <img src={idExample} alt='id example' />
                </ImageHolder>
                <div>
                  <ExampleTitle>
                    <IconCircleCheckmark />
                    <p>{t('goodExample')}</p>
                  </ExampleTitle>
                  <div>
                    <Paragraph $fontSize='1.2rem' $marginBottom='0rem'>
                      {t('clearlyShowsIdDocumentFully')}
                    </Paragraph>
                  </div>
                </div>
              </PhotoExample>
              <Paragraph $fontSize='1.2rem' $marginBottom='0rem'>
                {t('theImageShouldNotBeEdited')}
              </Paragraph>
            </div>
          </PhotoSection>

          <PhotoSection>
            <PhotoSectionTitle>
              <h3>{t('photoOfHoldingYourId')}</h3>
              {/* <span className='circle'>?</span> */}
            </PhotoSectionTitle>
            <Paragraph $fontSize='1.2rem'>{t('pleaseUploadAPhotoHoldingYourId')}</Paragraph>

            <input
              onChange={handleHoldImageChange}
              type='file'
              id='idHoldingFile'
              className='file'
              hidden
              multiple
            />
            <SelectFileButton htmlFor='idHoldingFile'>{t('selectFile')}</SelectFileButton>

            {!(idHoldProgressValues.length === 0) && (
              <PhotoUploadList>
                {idHoldProgressValues.map((file, index) => {
                  return (
                    <PhotoUploadCard
                      key={index}
                      name={file.name}
                      progressValue={file.percentage}
                      type={file.type}
                      size={file.size}
                      abortFn={() => {
                        file.cancel?.()
                      }}
                    />
                  )
                })}
              </PhotoUploadList>
            )}
            <div>
              <PhotoExample>
                <ImageHolder>
                  <img src={idHoldingExample} alt={t('idExample')} />
                </ImageHolder>
                <div>
                  <ExampleTitle>
                    <IconCircleCheckmark />
                    <p>{t('goodExample')}</p>
                  </ExampleTitle>
                  <div>
                    <Paragraph $fontSize='1.2rem' $marginBottom='0rem'>
                      {t('asWellAsClearImageOfTheUploadedId')}
                    </Paragraph>
                  </div>
                </div>
              </PhotoExample>
            </div>
          </PhotoSection>

          <InputContainer>
            <InputLabel $filled={!!formik.values.idExpiration} htmlFor='idExpiration'>
              {t('iDExpirationDate')}
            </InputLabel>
            <Input
              key='idExpiration'
              value={formik.values.idExpiration}
              onClick={() => {
                if (!formik.values.noExpiration) {
                  openDatePickerModal(t('idExpiration'), 'idExpiration', true)
                }
              }}
              $filled={!!formik.values.idExpiration}
              readOnly
              disabled={formik.values.noExpiration}
            />
          </InputContainer>

          <RadioContainer>
            <RadioButton
              active={formik.values.noExpiration ? 1 : 0}
              clickFn={async () => {
                const nextValue = !formik.values.noExpiration

                await formik.setFieldValue('noExpiration', nextValue)

                if (nextValue) {
                  await formik.setFieldValue('idExpiration', '')
                  await formik.setFieldTouched('idExpiration', false, false)
                  formik.setFieldError('idExpiration', undefined)
                }

                await formik.setFieldTouched('noExpiration', true, false)
              }}
            />
            <p>{t('noExpirationDate')}</p>
          </RadioContainer>
        </>
      )}

      <AgreementContainer aria-required='true'>
        <RadioButton
          active={formik.values.agreeToCreatorTerms ? 1 : 0}
          clickFn={async () => {
            await formik.setFieldValue(
              'agreeToCreatorTerms',
              !formik.values.agreeToCreatorTerms
            )
            await formik.setFieldTouched('agreeToCreatorTerms', true, false)
          }}
        />
        <AgreementText>
          By submitting this application, you agree to the{' '}
          <BlueLink to='/creatorterms' target='_blank' rel='noopener noreferrer'>
            Creator Terms
          </BlueLink>
          .
        </AgreementText>
      </AgreementContainer>

      <Paragraph $fontSize='1.2rem' $type='error'>
        {formik.touched.agreeToCreatorTerms && !formik.values.agreeToCreatorTerms
          ? 'You must agree to the Creator Terms'
          : ''}
      </Paragraph>
    </form>
  )
}

const FormDivider = styled.div`
  height: 0.1rem;
  width: 100%;
  background: ${colors.lightGray};
  margin: 2.4rem auto;
`

interface ParagraphProps {
  $fontSize?: string
  $type?: 'error'
  $right?: boolean
  $marginBottom?: string
}

const Paragraph = styled.p<ParagraphProps>`
  font-family: 'Montserrat', sans-serif;
  font-style: normal;
  font-weight: 500;
  letter-spacing: 0.02em;
  font-size: ${props => props.$fontSize ?? '1.4rem'};
  line-height: 1.7rem;
  margin-top: 0;
  margin-bottom: ${props => props.$marginBottom ?? '1.7rem'};
  padding-left: 0.5rem;
  padding-right: 0.5rem;
  color: ${props => (props.$type === 'error' ? colors.red : colors.grey)};
  ${props => (props.$right ? 'text-align: right;' : '')}
`

const AgreementText = styled.p`
  padding: 0.5rem 0;
  font-family: 'Montserrat', sans-serif;
  font-size: 1.4rem;
  color: ${colors.grey};

  a {
    color: #339afa;
    text-decoration: underline;

    &:hover {
      text-decoration: none;
    }
  }
`

const BlueLink = styled(Link)`
  color: #339afa;

  &:hover {
    text-decoration: none;
  }
`

const AutocompleteWrapper = styled.div`
  position: relative;
  margin-bottom: 1rem;
`

const InputContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  height: 6rem;
  background: ${colors.white89deg};
  border: 0.1rem solid #dddddd;
  box-sizing: border-box;
  box-shadow: ${colors.shadowBlack};
  border-radius: 2rem;
  margin-bottom: 1rem;
`

interface InputProps {
  $filled: boolean
}

const InputLabel = styled.label<InputProps>`
  font-family: 'Montserrat', sans-serif;
  font-style: normal;
  letter-spacing: 0.02em;
  font-size: 1.2rem;
  line-height: 1.5rem;
  font-weight: 500;
  position: absolute;
  left: 2rem;
  color: #b0b0b0;
  pointer-events: none;
  transition: 0.2s all;
  transform: translateY(0);
  z-index: 1;
  ${props => !!props.$filled && 'top: 1.6rem;'}
`

const Input = styled.input<InputProps>`
  font-family: 'Montserrat', sans-serif;
  font-style: normal;
  font-weight: 600;
  letter-spacing: 0.02em;
  font-size: 1.4rem;
  line-height: 1.7rem;
  text-indent: 1.8rem;
  height: 100%;
  width: 100%;
  border-radius: 2rem;
  border: 0.2rem solid transparent;
  transition: border 0.2s, padding 0.2s;
  ${props => !!props.$filled && 'padding-top: 2rem;'}
`

const DocumentSelectContainer = styled.div<{ $open: boolean }>`
  position: relative;
  background: ${colors.white89deg};
  border: 0.1rem solid #dddddd;
  box-sizing: border-box;
  box-shadow: ${colors.shadowBlack};
  border-radius: 2rem;
  margin-bottom: 1rem;
  overflow: hidden;
  transition: border 0.2s ease, box-shadow 0.2s ease;

  ${props =>
    props.$open &&
    `
      border-color: #dddddd;
    `}
`

const DocumentSelectHeader = styled.button`
  position: relative;
  display: block;
  width: 100%;
  height: 6rem;
  background: transparent;
  border: none;
  padding: 0;
  text-align: left;
  cursor: pointer;
`

const DocumentSelectLabel = styled.span<InputProps>`
  font-family: 'Montserrat', sans-serif;
  font-style: normal;
  letter-spacing: 0.02em;
  font-size: 1.2rem;
  line-height: 1.5rem;
  font-weight: 500;
  position: absolute;
  left: 2rem;
  color: #b0b0b0;
  pointer-events: none;
  transition: 0.2s all;
  transform: translateY(0);
  z-index: 1;
  ${props => !!props.$filled && 'top: 1.6rem;'}
`

const DocumentSelectValue = styled.span`
  display: flex;
  align-items: center;
  height: 100%;
  padding-left: 1.8rem;
  padding-right: 1.8rem;
  padding-top: 2rem;
  font-family: 'Montserrat', sans-serif;
  font-style: normal;
  font-weight: 600;
  letter-spacing: 0.02em;
  font-size: 1.4rem;
  line-height: 1.7rem;
  color: #181d3a;
`

const DocumentOptions = styled.div`
  width: 100%;
  background: transparent;
`

const DocumentOption = styled.button<{ $active: boolean }>`
  width: 100%;
  min-height: 6rem;
  border: none;
  background: ${props => (props.$active ? '#f3f3f3' : 'transparent')};
  color: #181d3a;
  font-family: 'Montserrat', sans-serif;
  font-size: 1.4rem;
  font-weight: 600;
  text-align: left;
  padding: 1.8rem;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background: #f3f3f3;
  }
`

const SuggestionsDropdown = styled.div`
  position: absolute;
  top: calc(100% - 0.4rem);
  left: 0;
  right: 0;
  background: ${colors.white};
  border: 0.1rem solid #d9dde5;
  border-radius: 1.6rem;
  box-shadow: 0 1rem 2rem rgba(0, 0, 0, 0.08);
  max-height: 28rem;
  overflow-y: auto;
  z-index: 20;
`

const SuggestionItem = styled.button`
  width: 100%;
  display: block;
  text-align: left;
  background: transparent;
  border: none;
  padding: 1.2rem 1.4rem;
  cursor: pointer;

  &:hover {
    background: #f6f8fb;
  }
`

const SuggestionMain = styled.div`
  font-size: 1.4rem;
  font-weight: 600;
  color: ${colors.blueDark};
`

const SuggestionSecondary = styled.div`
  font-size: 1.25rem;
  color: ${colors.grey};
  margin-top: 0.2rem;
`

const SuggestionLoading = styled.div`
  padding: 1.2rem 1.4rem;
  font-size: 1.3rem;
  color: ${colors.grey};
`

const RadioContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  padding-left: 0.5rem;
  padding-top: 1rem;
  margin-bottom: 0.5rem;

  p {
    font-weight: 500;
    font-size: 1.3rem;
    margin: 0 0 0 1rem;
  }
`

const AgreementContainer = styled(RadioContainer)`
  margin-bottom: 1.2rem;
`

const PhotoSection = styled.div`
  background: ${colors.white89deg};
  border: 0.1rem solid #dddddd;
  box-sizing: border-box;
  box-shadow: ${colors.whiteSmoke};
  border-radius: 2rem;
  padding: 2rem 2rem;
  margin-bottom: 1.1rem;
`

const PhotoSectionTitle = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  margin-bottom: 1rem;

  .circle {
    display: flex;
    width: 1.6rem;
    height: 1.6rem;
    justify-content: center;
    align-items: center;
    background: #b8b8b8;
    color: ${colors.white};
    border-radius: 2rem;
    margin-left: 1rem;
  }

  h3 {
    font-family: 'Montserrat', sans-serif;
    font-style: normal;
    font-weight: 600;
    letter-spacing: 0.02em;
    font-size: 1.6rem;
    color: ${colors.blueDark};
    margin: 0;
  }
`

const SelectFileButton = styled.label`
  display: block;
  margin-bottom: 2.4rem;
  border-radius: 16rem;
  background: ${colors.black157deg};
  width: 100%;
  padding: 1rem;
  color: ${colors.white};
  font-family: 'Montserrat', sans-serif;
  font-style: normal;
  font-weight: 500;
  letter-spacing: 0.02em;
  font-size: 1.4rem;
  line-height: 1.7rem;
  text-align: center;
`

const PhotoExample = styled.div`
  display: flex;
  margin-bottom: 1.4rem;

  @media (max-width: ${screens.extraSmall}) {
    flex-wrap: wrap;
    justify-content: center;
  }

  & > .image {
    margin-right: 2rem;
    margin-bottom: 1rem;
    max-width: 100%;

    & > img {
      width: 100%;
      height: auto;
    }
  }
`

const ExampleTitle = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  margin-bottom: 1.1rem;
  font-family: 'Montserrat', sans-serif;
  font-style: normal;
  font-weight: 600;
  letter-spacing: 0.02em;
  font-size: 1.4rem;
  line-height: 1.7rem;

  & > p {
    color: #26b99a;
    margin: 0;
  }

  & > svg {
    margin-right: 0.9rem;
  }
`

const ImageHolder = styled.div`
  margin-right: 2rem;
  margin-bottom: 1rem;
  max-width: 100%;
`

const PhotoUploadList = styled.div`
  margin-bottom: 4.3rem;
`

const GroupWithBorder = styled.div`
  border: 0.1rem solid #dddddd;
  box-sizing: border-box;
  border-radius: 2rem;
  padding: 1.5rem 1rem;
  margin-bottom: 1rem;
`

const TwoColumnRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0 1rem;
  margin-bottom: 1rem;

  @media (max-width: ${screens.extraSmall}) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }

  & > ${InputContainer} {
    margin-bottom: 1rem;
  }
`